import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import "./QuotationBuilder.css";

const QuotationBuilder = () => {
  const [materialHeadings, setMaterialHeadings] = useState([]);
  const [fixedHeadings, setFixedHeadings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedHeading, setSelectedHeading] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [cartage, setCartage] = useState("");
  const [installation, setInstallation] = useState("");
  const [wastage, setWastage] = useState("5");
  const [profit, setProfit] = useState("");
  const [quotationList, setQuotationList] = useState([]);

  // New states for fixed characteristic/make
  const [selectedCharacteristic, setSelectedCharacteristic] = useState(null);
  const [selectedMake, setSelectedMake] = useState(null);
  const [makePrice, setMakePrice] = useState(0);

  const BASE_URL = "https://ideatorportal.in/api/";

  useEffect(() => {
    axios.get(`${BASE_URL}get_headings.php`).then((res) => {
      setMaterialHeadings(res.data);
    });
    axios.get(`${BASE_URL}list_fixed_heading.php`).then((res) => {
      setFixedHeadings(res.data);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".search-container")) {
        setSearchTerm("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const allHeadings = [
    ...materialHeadings.map((h) => ({ ...h, type: "material" })),
    ...fixedHeadings.map((h) => ({ ...h, type: "fixed" })),
  ];

  const filteredHeadings = allHeadings.filter((h) =>
    h.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (heading) => {
    setSelectedHeading(heading);
    setSearchTerm(heading.name);

    // Reset dropdowns
    setSelectedCharacteristic(null);
    setSelectedMake(null);
    setMakePrice(0);
  };

  const handleAdd = async () => {
    if (!selectedHeading || isNaN(parseFloat(quantity))) {
      return alert("Please select a heading and enter a valid quantity.");
    }
  
    if (selectedHeading.type === "fixed") {
      if (!selectedCharacteristic || !selectedMake || makePrice == null) {
        return alert("Please select characteristic and make with valid price.");
      }
  
      const finalCost =
  ((makePrice * parseFloat(quantity || 1)) * (1 + parseFloat(wastage || 0) / 100) +
    parseFloat(cartage || 0) +
    parseFloat(installation || 0)) *
  (1 + parseFloat(profit || 0) / 100);

  
      setQuotationList((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "fixed",
          heading: `${selectedHeading.name} - ${selectedCharacteristic} - ${selectedMake}`,
          unit: selectedHeading.unit,
          inputQty: quantity,
          final_cost: finalCost,
        },
      ]);
    } else {
      // Material type
      try {
        const res = await axios.post(`${BASE_URL}calculate_quotation.php`, {
          type: selectedHeading.type,
          id: selectedHeading.id,
          quantity: parseFloat(quantity),
          cartage: parseFloat(cartage) || 0,
          installation: parseFloat(installation) || 0,
          profit: parseFloat(profit) || 0,
          wastage: parseFloat(wastage) || 0,
        });
  
        if (res.data.success) {
          const { final_cost = 0 } = res.data;
  
          setQuotationList((prev) => [
            ...prev,
            {
              id: Date.now(),
              type: "material",
              heading: selectedHeading.name,
              unit: selectedHeading.unit,
              inputQty: quantity,
              final_cost: Number(final_cost) || 0,
            },
          ]);
        } else {
          alert(res.data.message || "Quotation calculation failed.");
        }
      } catch (err) {
        console.error("Calculation Error:", err);
        alert("Error calculating quotation.");
      }
    }
  
    // Reset all inputs
    setSelectedHeading(null);
    setQuantity("");
    setCartage("");
    setInstallation("");
    setProfit("");
    setSelectedCharacteristic(null);
    setSelectedMake(null);
    setMakePrice(0);
  };
  

  const handleRemove = (id) => {
    setQuotationList((prev) => prev.filter((item) => item.id !== id));
  };

  const exportToExcel = () => {
    const rows = quotationList.map((item) => ({
      Heading: item.heading,
      Quantity: item.inputQty,
      Unit: item.unit,
      "Final Cost (₹)": item.final_cost.toFixed(2),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Quotation");
    XLSX.writeFile(wb, "quotation.xlsx");
  };

  return (
    <div className="quotation-builder">
      <h2>Quotation Assistant</h2>

      <div className="controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search heading..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelectedHeading(null);
            }}
          />
          {searchTerm && filteredHeadings.length > 0 && !selectedHeading && (
            <ul className="search-dropdown">
              {filteredHeadings.map((h) => (
                <li key={`${h.type}-${h.id}`} onClick={() => handleSelect(h)}>
                  {h.name} <span className="type-badge">{h.type}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selectedHeading && (
          <div className="input-fields">
            <p>
              Selected: <strong>{selectedHeading.name}</strong> ({selectedHeading.type})
            </p>

            <label>
              Quantity ({selectedHeading.unit})
              <input
                type="text"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>

            <label>
              Cartage (₹)
              <input
                type="text"
                value={cartage}
                onChange={(e) => setCartage(e.target.value)}
              />
            </label>

            <label>
              Installation (₹)
              <input
                type="text"
                value={installation}
                onChange={(e) => setInstallation(e.target.value)}
              />
            </label>

            
              <label>
                Wastage (%)
                <input
                  type="text"
                  value={wastage}
                  onChange={(e) => setWastage(e.target.value)}
                />
              </label>
        

            <label>
              Profit (%)
              <input
                type="text"
                value={profit}
                onChange={(e) => setProfit(e.target.value)}
              />
            </label>

            {/* Fixed heading: Characteristic + Make dropdown */}
            {selectedHeading.type === "fixed" && (
              <>
                <label>
                  Characteristic
                  <select
                    value={selectedCharacteristic || ""}
                    onChange={(e) => {
                      const charName = e.target.value;
                      setSelectedCharacteristic(charName);
                      setSelectedMake(null);
                      setMakePrice(0);
                    }}
                  >
                    <option value="">-- Select Characteristic --</option>
                    {selectedHeading.characteristics?.map((char, i) => (
                      <option key={i} value={char.characteristic}>
                        {char.characteristic}
                      </option>
                    ))}
                  </select>
                </label>

                {selectedCharacteristic && (
  <label>
    Make
    <select
      value={selectedMake || ""}
      onChange={(e) => {
        const makeName = e.target.value;
        setSelectedMake(makeName);

        const makes =
          selectedHeading.characteristics.find(
            (c) => c.characteristic === selectedCharacteristic
          )?.makes || [];

        if (makeName === "Average") {
          const avg =
            makes.reduce((sum, m) => sum + parseFloat(m.price_per_unit), 0) /
            makes.length;
          setMakePrice(Number.isFinite(avg) ? avg : 0);
        } else {
          const selected = makes.find((m) => m.make_name === makeName);
          setMakePrice(selected?.price_per_unit || 0);
        }
      }}
    >
      <option value="">-- Select Make --</option>
      <option value="Average">Average</option>
      {selectedHeading.characteristics
        .find((c) => c.characteristic === selectedCharacteristic)
        ?.makes.map((make, i) => (
          <option key={i} value={make.make_name}>
            {make.make_name}
          </option>
        ))}
    </select>
  </label>
)}


                {selectedMake && (
                  <p style={{ marginTop: "5px" }}>
                    Price per unit: <strong>₹{makePrice}</strong>
                  </p>
                )}
              </>
            )}

            <button onClick={handleAdd}>Add</button>
          </div>
        )}
      </div>

      <div className="quotation-table">
        {quotationList.map((item) => (
          <div className="quotation-card" key={item.id}>
            <h4>{item.heading}</h4>
            <p>
              Quantity: {item.inputQty} {item.unit}
            </p>
            <h5>
              Final Cost: ₹
              {typeof item.final_cost === "number" && !isNaN(item.final_cost)
                ? item.final_cost.toFixed(2)
                : "Not Available"}
            </h5>
            <button
              className="remove-btn"
              onClick={() => handleRemove(item.id)}
              title="Remove item"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {quotationList.length > 0 && (
        <div className="export-section">
          <button className="export-btn" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div>
      )}
    </div>
  );
};

export default QuotationBuilder;
